import './AdTargeting.sass';
import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  BottomBar,
  Button,
  Collapse,
  Form,
  FormItem,
  Header,
  Input,
  List,
  ListItem,
  Placeholder,
  ScrollView,
  SegmentedControl,
  Select,
  Switch,
} from '$uikit';
import { useTranslator, useWikiLink } from '$hooks';
import { CurrencyEnum } from '$store/models';
import { LANGUAGE_MAP } from '$shared/constants/languages';
import { Loader, MetaTags, Tooltip } from '$shared/components';
import { goBack, showModal } from '$navigation/helper';
import { useDispatch, useSelector } from 'react-redux';
import { adsActions, adsSelector } from '$store/ads';
import { NEW_AD } from '$shared/constants/modals';
import { getDocumentHref } from '$utils';
import { AdTargetingMeter } from '$core/Ads/AdTargetingMeter/AdTargetingMeter';
import { amountFractionDigits, formatInputAmount } from '$utils/number';
import { Api } from '$api';
import { toastActions } from '$store/toast';
import { batchActions } from '$store';
import { IcChevron16 } from '$assets';

export interface AdTargetingProps {
  adId?: number;
}

export const AdTargeting: FC<AdTargetingProps> = ({ adId }) => {
  const t = useTranslator();

  const dispatch = useDispatch();
  const [loadingStatus, setLoadingStatus] = useState(adId && adId > 0 ? 'loading' : '');

  const { objectId, adType } = useSelector(adsSelector);
  const [priceLoading, setPriceLoading] = useState(true);
  const [minPrice, setMinPrice] = useState(10);
  const [maxPrice, setMaxPrice] = useState(10);

  const [minPricePlace, setMinPricePlace] = useState(10);
  const [maxPricePlace, setMaxPricePlace] = useState(10);

  const [targetSubscribed, setTargetSubscribed] = useState(true);

  const [price, setPrice] = useState('1');
  const [currency, setCurrency] = useState(CurrencyEnum.TON);
  const digits = amountFractionDigits(currency);
  const handlePriceChange = ({ target }) => {
    setPrice(formatInputAmount(target.value, digits));
  };
  const handleCurrencyChange = (next: CurrencyEnum) => {
    setCurrency(next);
    setPrice((prev) => formatInputAmount(String(prev), amountFractionDigits(next)));
  };

  const [additionalSettingsOpen, setAdditionalSettingsOpen] = useState(false);
  const [targetLang, setTargetLang] = useState('all');
  const [spendLimitEnabled, setSpendLimitEnabled] = useState(false);
  const [spendLimit, setSpendLimit] = useState('100');
  const handleSpendLimitChange = ({ target }) => {
    setSpendLimit(formatInputAmount(target.value, digits));
  };

  const loadPrice = useCallback(() => {
    setPriceLoading(true);
    Api.post<any, any>('/ads/price/info', { targetSubscribed, targetLang })
      .then((res) => {
        setMinPrice(res.minPrice);
        setMaxPrice(res.avgPrice);
        setMinPricePlace(res.minPricePlace);
        setMaxPricePlace(res.avgPricePlace);
        setPriceLoading(false);
      })
      .catch(() => {});
  }, [targetSubscribed, targetLang]);

  useEffect(() => {
    loadPrice();
  }, [targetSubscribed, targetLang]);

  const loadData = useCallback(() => {
    setLoadingStatus('loading');
    Api.get<any, any>(`/ads/${adId}/info`)
      .then((data) => {
        const ad = data.ads;
        const cur = ad.currency || CurrencyEnum.TON;
        const d = amountFractionDigits(cur);
        dispatch(
          batchActions(
            adsActions.setAdType(ad.objectType),
            adsActions.setObjectId(ad.objectId),
            ...(typeof data.balance === 'number'
              ? [adsActions.setAdsBalance(data.balance)]
              : []),
            ...(typeof data.placeBalance === 'number'
              ? [adsActions.setPlaceBalance(data.placeBalance)]
              : []),
          ),
        );
        setTargetSubscribed(ad.targetSubscribed);
        setCurrency(cur);
        setPrice(formatInputAmount(String(ad.price), d));
        setTargetLang(ad.targetLang);
        setSpendLimitEnabled(ad.spendLimit > 0);
        setSpendLimit(formatInputAmount(String(ad.spendLimit), d));
        setLoadingStatus('');
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
        setLoadingStatus('failed');
      });
  }, [adId, dispatch]);

  useEffect(() => {
    if (adId && adId > 0) loadData();
  }, [adId, loadData]);

  useEffect(() => {
    if (!adId && (!adType || !objectId)) showModal(NEW_AD, true);
  }, [adType, objectId, adId]);

  const handlePublish = () => {
    dispatch(
      adsActions.createAd({
        price: +price,
        spendLimit: spendLimitEnabled ? +spendLimit : 0,
        targetLang,
        targetSubscribed,
        adId: adId || 0,
        currency,
      }),
    );
  };

  function renderContent() {
    if (loadingStatus === 'loading') {
      return <Loader isWrapped />;
    }

    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadData}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    return (
      <>
        <ScrollView>
          <AdTargetingMeter
            value={+price}
            max={currency === CurrencyEnum.PLACE ? maxPricePlace : maxPrice}
            min={currency === CurrencyEnum.PLACE ? minPricePlace : minPrice}
            isLoading={priceLoading}
          />

          <Form>
            <FormItem
              title={t('ad_targeting_show_to_label')}
              description={
                targetSubscribed
                  ? t('ad_targeting_show_to_paying_description')
                  : t('ad_targeting_show_to_unpaid_description')
              }
            >
              <SegmentedControl
                onChange={() => setTargetSubscribed(!targetSubscribed)}
                defaultValue={targetSubscribed}
                options={[
                  { label: t('ad_targeting_unpaid_users'), value: false },
                  { label: t('ad_targeting_paying_users'), value: true },
                ]}
              />
            </FormItem>

            {!adId && (
              <FormItem>
                <SegmentedControl
                  options={[
                    { label: 'TON', value: CurrencyEnum.TON },
                    { label: 'PLACE', value: CurrencyEnum.PLACE },
                  ]}
                  defaultValue={currency}
                  onChange={handleCurrencyChange}
                />
              </FormItem>
            )}

            <FormItem
              title={t('ad_targeting_price_label')}
              description={t('ad_targeting_price_description')}
            >
              <Input
                type="price"
                currency={currency}
                currencyAlign="end"
                value={price}
                onChange={handlePriceChange}
              />
            </FormItem>

            <Collapse
              title={t('ad_targeting_additional_settings_label')}
              onClick={() => setAdditionalSettingsOpen(!additionalSettingsOpen)}
              isOpen={additionalSettingsOpen}
              className="AdTargeting__additionalSettings"
            >
              <FormItem title={t('ad_targeting_lang_label')} skipPadding>
                <Select
                  value={targetLang}
                  onChange={({ target }) => setTargetLang(target.value)}
                >
                  {['all', ...Object.keys(LANGUAGE_MAP)].map((lang) => (
                    <option key={lang} value={lang}>
                      {lang === 'all' ? t('ad_targeting_lang_all') : LANGUAGE_MAP[lang]}
                    </option>
                  ))}
                </Select>
              </FormItem>

              <FormItem skipPadding minimize>
                <List>
                  <ListItem
                    after={<Switch checked={spendLimitEnabled} />}
                    onClick={() => setSpendLimitEnabled(!spendLimitEnabled)}
                    disableHighlight
                  >
                    {t('ad_targeting_spend_limit')}
                  </ListItem>
                </List>
              </FormItem>

              {spendLimitEnabled && (
                <FormItem skipPadding>
                  <Input
                    type="price"
                    currency={currency}
                    currencyAlign="end"
                    value={spendLimit}
                    onChange={handleSpendLimitChange}
                  />
                </FormItem>
              )}
            </Collapse>
          </Form>
        </ScrollView>

        <BottomBar direction="column">
          <Button stretched onClick={handlePublish} mode="default">
            {adId ? t('ad_targeting_save_and_publish') : t('ad_targeting_publish')}
          </Button>
          <div className="AdTargeting__terms">
            {t('promote_post_terms_pref')}{' '}
            <a href={getDocumentHref('ads_terms')} target="_blank" rel="noreferrer">
              {t('promote_post_terms_link')}
            </a>
          </div>
        </BottomBar>
      </>
    );
  }

  const title = adId ? t('ad_targeting_title') : t('ad_new_targeting_title');
  const wikiLink = useWikiLink('/for-creators/advertising-platform');
  const tooltip = (
    <Tooltip
      iconSize={28}
      title={t('ad_targeting_tooltip_title')}
      text={t('ad_targeting_tooltip_text')}
      action={
        <Button stretched iconRight={<IcChevron16 />} href={wikiLink} target="_blank">
          {t('ads_tooltip_open_support')}
        </Button>
      }
    />
  );

  return (
    <>
      <MetaTags title={title} />
      <Header
        showBackButton
        backButtonOnClick={() => goBack()}
        rightIcons={[{ icon: tooltip }]}
      >
        {title}
      </Header>
      {renderContent()}
    </>
  );
};
